/**
 * RTK Query endpoints — Dashboard + Calendar
 *
 * Migrated from: src/pages/dashboard/actions.js
 *
 * Three read-only dashboard queries (cards, charts, recent controls) plus
 * the calendar surface (events + controllers) and its mutations
 * (controller color, event cancel, drag-drop reschedule, schedule).
 *
 * Schedule and the calendar mutations invalidate both `CalendarEvent` and
 * `Inspection` tags so the inspections list refetches automatically when
 * an event lifecycle changes.
 */

import { baseApi } from './baseApi';
import { buildQueryString } from './utils';

const dashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    dashboardCards: builder.query({
      query: () => ({ url: '/su/dashboard/cards', method: 'GET' }),
      providesTags: [{ type: 'DashboardCard', id: 'ALL' }],
    }),

    dashboardCharts: builder.query({
      query: () => ({ url: '/su/dashboard/charts', method: 'GET' }),
      providesTags: [{ type: 'DashboardChart', id: 'ALL' }],
    }),

    recentControls: builder.query({
      query: () => ({ url: '/su/dashboard/recent-controls', method: 'GET' }),
      providesTags: [{ type: 'RecentControl', id: 'ALL' }],
    }),

    calendarEvents: builder.query({
      query: (params = {}) => ({
        url: `/su/calendar/events${buildQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'CalendarEvent', id: 'LIST' }],
    }),

    calendarControllers: builder.query({
      query: () => ({ url: '/su/calendar/controllers', method: 'GET' }),
      providesTags: [{ type: 'CalendarController', id: 'LIST' }],
    }),

    updateControllerColor: builder.mutation({
      query: ({ userId, color }) => ({
        url: `/su/calendar/controllers/${userId}/color`,
        method: 'PATCH',
        body: { color },
      }),
      invalidatesTags: [{ type: 'CalendarController', id: 'LIST' }],
    }),

    cancelCalendarEvent: builder.mutation({
      query: ({ type, controlId }) => ({
        url: `/su/calendar/events/${type}/${controlId}/cancel`,
        method: 'PATCH',
        body: {},
      }),
      invalidatesTags: (_result, _error, { controlId }) => [
        { type: 'CalendarEvent', id: 'LIST' },
        { type: 'Inspection', id: controlId },
        { type: 'Inspection', id: 'LIST' },
        { type: 'RecentControl', id: 'ALL' },
      ],
    }),

    updateEventDate: builder.mutation({
      query: ({ controlId, newDate }) => ({
        url: `/su/calendar/events/${controlId}/date`,
        method: 'PATCH',
        body: { newDate },
      }),
      invalidatesTags: (_result, _error, { controlId }) => [
        { type: 'CalendarEvent', id: 'LIST' },
        { type: 'Inspection', id: controlId },
        { type: 'Inspection', id: 'LIST' },
      ],
    }),

    scheduleInspection: builder.mutation({
      query: (data) => ({
        url: '/su/control/schedule',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'CalendarEvent', id: 'LIST' },
        { type: 'Inspection', id: 'LIST' },
        { type: 'DashboardCard', id: 'ALL' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useDashboardCardsQuery,
  useDashboardChartsQuery,
  useRecentControlsQuery,
  useCalendarEventsQuery,
  useLazyCalendarEventsQuery,
  useCalendarControllersQuery,
  useLazyCalendarControllersQuery,
  useUpdateControllerColorMutation,
  useCancelCalendarEventMutation,
  useUpdateEventDateMutation,
  useScheduleInspectionMutation,
} = dashboardApi;

export default dashboardApi;
